<template>
    <DModal
        is-big
        :title="__('tools.fulfillment.request_details')"
        :show="showModal"
        @close="closeModal"
    >
        <DCard>
            <DCardBody>
                <DRow>
                    <DCol>
                        <span class="fw-bold">{{ __('tools.fulfillment.module') }}:</span>
                        <span>{{ request.filter }}</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('tools.fulfillment.customer') }}:</span>
                        <span v-if="request.job_reference.customer.company">{{ request.job_reference.customer.company }}</span>
                        <span v-else>{{ request.job_reference.customer.firstname }} {{ request.job_reference.customer.lastname }}</span>
                        <span>({{ request.job_reference.customer.id }})</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('tools.fulfillment.created_at') }}:</span>
                        <span>{{ setDateFormat(request.created_at) }}</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('tools.fulfillment.status') }}:</span>
                        <span>{{ __('tools.fulfillment.status_' + request.status) }}</span>
                    </DCol>
                </DRow>
                <DRow>
                    <DCol>
                        <span class="fw-bold">{{ __('tools.fulfillment.action') }}:</span>
                        <span>{{ request.action.name }}</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('tools.fulfillment.contract') }}:</span>
                        <span>{{ request.product.name }} ({{ request.job_reference.id }})</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('tools.fulfillment.updated_at') }}:</span>
                        <span>{{ setDateFormat(request.updated_at) }}</span>
                    </DCol>
                    <DCol>
                        <span class="fw-bold">{{ __('tools.fulfillment.attempts') }}:</span>
                        <span>{{ attemptCount }}</span>
                    </DCol>
                </DRow>
            </DCardBody>
        </DCard>
        <DCard v-if="loading || loadFailed || attempts.length">
            <DCardBody>
                <div
                    v-if="loading"
                    class="text-center py-3"
                >
                    {{ __('messages.loading') }}
                </div>
                <div
                    v-else-if="loadFailed"
                    class="text-danger py-3"
                >
                    {{ __('administrations.queue_jobs.modal.tries_failed') }}
                </div>
                <template v-else>
                    <!--
                        Guards against listing fewer attempts than the reported total. Neither
                        mode is short today -- live rows arrive whole and the archive endpoint
                        returns every stored attempt -- so this does not currently render.
                    -->
                    <div
                        v-if="attemptCount > attempts.length"
                        class="text-muted pb-2"
                    >
                        {{ __('administrations.queue_jobs.modal.tries_truncated', { shown: attempts.length, total: attemptCount }) }}
                    </div>
                    <DTable
                        :items="attempts"
                        :fields="fields"
                    >
                        <template #updated_at="{item}">
                            <td>{{ setDateFormat(item.updated_at, 'YYYY-MM-DD hh:mm A') }}</td>
                        </template>
                        <template #status="{item}">
                            <td v-if="item.successful === true">
                                {{ __('tools.fulfillment.status_completed') }}
                            </td>
                            <td v-else>
                                {{ __('tools.fulfillment.status_failed') }}
                            </td>
                        </template>
                        <template #error="{item}">
                            <td>
                                <div
                                    v-for="error in nl2array(item.error)"
                                    :key="error"
                                >
                                    {{ error }}
                                </div>
                            </td>
                        </template>
                    </DTable>
                </template>
            </DCardBody>
        </DCard>
    </DModal>
</template>

<script>
import _ from 'lodash'
import nl2array from '~@/Mixins/nl2array'

export default {
    name: 'FulfillmentRequestDetailsModal',
    mixins: [nl2array],
    props: {
        request: {
            type: Object,
            default: () => { return {} }
        },
        showModal: {
            type: Boolean,
            default: false
        },
        /**
         * Archive mode fetches attempts when this opens rather than with the row -- a record
         * holds every attempt a request ever had. The live path still eager-loads them.
         */
        archived: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close-modal'],
    data () {
        return {
            fields: [
                { key: 'updated_at', label: this.__('tools.fulfillment.date') },
                { key: 'status', label: this.__('tools.fulfillment.status') },
                { key: 'error', label: this.__('tools.fulfillment.error') },
                { key: 'crm_request_id', label: this.__('tools.fulfillment.request_id') }
            ],
            fetched: null,
            fetchedTotal: 0,
            loading: false,
            loadFailed: false
        }
    },
    computed: {
        attempts () {
            return _.sortBy(this.fetched ?? this.request.tries ?? [], ['updated_at']).reverse()
        },
        /** Archive mode reports the total separately; live rows carry every attempt inline. */
        attemptCount () {
            return this.archived ? this.fetchedTotal : (this.request.tries?.length ?? 0)
        }
    },
    mounted () {
        if (this.archived) {
            this.loadArchivedTries()
        }
    },
    methods: {
        async loadArchivedTries () {
            this.loading = true
            this.loadFailed = false

            try {
                const response = await fetch(
                    this.$route('administrations.queue.archive-tries', { requestId: this.request.id }),
                    { headers: { Accept: 'application/json' } }
                )

                if (!response.ok) {
                    throw new Error(`Unexpected response ${response.status}`)
                }

                const payload = await response.json()

                // A degraded read answers 200 with an empty list, so this flag is all that
                // separates "unreadable" from "this request had none".
                if (payload.readFailed === true) {
                    this.loadFailed = true

                    return
                }

                this.fetched = payload.tries ?? []
                this.fetchedTotal = payload.total ?? this.fetched.length
            } catch {
                this.loadFailed = true
            } finally {
                this.loading = false
            }
        },
        closeModal () {
            this.$emit('close-modal')
        }
    }

}
</script>

<style scoped>
</style>
